import { ServiceAccountEntity } from '../../../ServiceAccountsTypes';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { EmptyStateProps, Icon, Icons, Table, TableColumn, TwoLinesCell } from '@silverfort/sf-components';
import { bigNumberToText } from '../../../../../../components/utils';
import moment from 'moment';
import { SortDirection } from '@silverfort/sf-components/dist/Table/interface';
import useGetRefSize from '../../../../../../hooks/useGetRefSize';
import { useServiceAccountContext } from '../../../contexts/ServiceAccountContext';
import ServiceAccountsTuples from './ServiceAccountsTuples/ServiceAccountsTuples';

interface Protocol {
    column: string;
    name: string;
    last_use?: number;
}

const TABS = [
    { name: 'Sources', value: 0 },
    { name: 'Destinations', value: 1 },
    { name: 'Protocols', value: 2 },
    { name: 'Authentications', value: 3 }
];

const PROTOCOLS: Protocol[] = [
    { column: 'last_use_kerberos', name: 'Kerberos' },
    { column: 'last_use_ntlm', name: 'NTLM' },
    { column: 'last_use_ldap_ldaps', name: 'LDAP/S' }
];

const ENTITY_TYPE_TO_ICON = {
    ip: Icons.IP,
    name: Icons.Server,
    guid: Icons.Server
};

const ENTITY_COLUMNS: TableColumn<ServiceAccountEntity>[] = [
    {
        columnKey: 'identifier',
        title: 'Name',
        columnClassName: 'w-1/2',
        render: (data) => (
            <TwoLinesCell
                topText={data.name || data.identifier}
                bottomText={data.domain_name ?? ''}
                leftIcon={() => <Icon icon={ENTITY_TYPE_TO_ICON[data.identifier_type]} className="text-sf-gray-400" />}
            />
        )
    },
    {
        columnKey: 'last_use',
        title: 'Last Seen',
        columnClassName: 'w-1/4',
        render: (data) => (data.last_use ? moment(data.last_use).format('MMM DD, YYYY') : 'Not seen')
    },
    {
        columnKey: 'hits',
        title: 'Authentications',
        columnClassName: 'w-1/4',
        render: (data) => bigNumberToText(data.hits)
    }
];

const PROTOCOL_COLUMNS: TableColumn<Protocol>[] = [
    {
        columnKey: 'name',
        title: 'Name'
    },
    {
        columnKey: 'last_use',
        title: 'Last Seen',
        render: (data) => (data.last_use ? moment(data.last_use).format('MMM DD, YYYY') : 'Not seen')
    }
];

interface SwitcherProps {
    selected: number;
    setSelected: (value: number) => void;
    tabs: { name: string; value: number }[];
}

const Switcher: FC<SwitcherProps> = ({ selected, setSelected, tabs }) => {
    const getClasses = useCallback(
        (tab) => {
            return `flex justify-center items-center h-8 w-28 rounded-[4px] cursor-pointer ${
                selected === tab ? 'bg-sf-gray-100 text-sf-gray-700' : 'text-sf-gray-500 hover:bg-sf-gray-50'
            }`;
        },
        [selected]
    );

    return (
        <div className="flex gap-1 items-center rounded-lg bg-white text-12">
            {tabs.map((tab) => (
                <div key={tab.name} className={getClasses(tab.value)} onClick={() => setSelected(tab.value)}>
                    {tab.name}
                </div>
            ))}
        </div>
    );
};

const ActivityTab = () => {
    const { updatedServiceAccount } = useServiceAccountContext();
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const tableRef = useRef(null);
    const { height: tableHeight } = useGetRefSize(tableRef);

    const onSort = useCallback(
        (columnKey: string, sortDirection: SortDirection, rows: Protocol[] | ServiceAccountEntity[]) => {
            return rows.sort((a, b) => {
                const valueA = a[columnKey];
                const valueB = b[columnKey];
                if (typeof valueA === 'number' && typeof valueB === 'number') {
                    return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
                } else if (typeof valueA === 'string' && typeof valueB === 'string') {
                    return sortDirection === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
                }
                return 0;
            });
        },
        []
    );

    const table = useMemo(() => {
        const emptyState: EmptyStateProps = {
            title: 'No activity detected',
            className: 'min-h-50'
        };

        const tableBodyHight = tableHeight ? tableHeight - 56 : 0;
        if (TABS[selectedTab].name === 'Sources') {
            const sources = onSort(
                'hits',
                'desc',
                updatedServiceAccount?.devices.filter((device) => !!device.hits) ?? []
            ) as ServiceAccountEntity[];

            return (
                <Table
                    columns={ENTITY_COLUMNS}
                    rows={sources}
                    EmptyStateProps={emptyState}
                    defaultSortColumn={'hits'}
                    defaultSortDirection={'desc'}
                    onSortChange={(column, direction) => onSort(column, direction, sources)}
                    bodyHeight={tableBodyHight}
                    rowHeight={40}
                />
            );
        }
        if (TABS[selectedTab].name === 'Destinations') {
            const destinations = onSort(
                'hits',
                'desc',
                updatedServiceAccount?.resources.filter((resource) => !!resource.hits) ?? []
            ) as ServiceAccountEntity[];

            return (
                <Table
                    columns={ENTITY_COLUMNS}
                    rows={destinations}
                    EmptyStateProps={emptyState}
                    defaultSortColumn={'hits'}
                    defaultSortDirection={'desc'}
                    onSortChange={(column, direction) => onSort(column, direction, destinations)}
                    bodyHeight={tableBodyHight}
                />
            );
        }
        if (TABS[selectedTab].name === 'Protocols') {
            const isProtocolsEmpty = PROTOCOLS.every((protocol) => !updatedServiceAccount?.[protocol.column]);
            const protocols = isProtocolsEmpty
                ? []
                : PROTOCOLS.map((protocol) => ({
                      ...protocol,
                      last_use: updatedServiceAccount?.[protocol.column]
                  }));
            return (
                <Table
                    EmptyStateProps={emptyState}
                    columns={PROTOCOL_COLUMNS}
                    rows={protocols}
                    onSortChange={(column, direction) => onSort(column, direction, protocols)}
                    bodyHeight={tableBodyHight}
                    rowHeight={40}
                />
            );
        }
        if (TABS[selectedTab].name === 'Authentications') {
            return <ServiceAccountsTuples tableBodyHeight={tableBodyHight} emptyState={emptyState} />;
        }
    }, [selectedTab, updatedServiceAccount, onSort, tableHeight]);
    return (
        <div className="flex flex-col overflow-y-auto h-full gap-4 text-sf">
            <div>
                <Switcher selected={selectedTab} setSelected={setSelectedTab} tabs={TABS} />
            </div>
            <div ref={tableRef} className="flex-1 min-h-0">
                {table}
            </div>
        </div>
    );
};
export default ActivityTab;
